import ExpeditionChapterHeading from "./ExpeditionChapterHeading";
import IconLink from "./IconLink";
import { siteUrl } from "./site";
import { expeditionLinks } from "./expeditionLinks";

const verletCode = `// Helper_Math.h: 기본 pow = 2일 때 Δt²를 사용합니다.
const Vec3 velocity = (currentPos - prevPos) * damping;
const float timeWeight = pow == 2
    ? timeDelta * timeDelta
    : std::pow(timeDelta, static_cast<float>(pow));
return currentPos + velocity + force * timeWeight;

// ChainModifier::Solve_Verlet에서 다음 위치를 계산하는 부분입니다.
Vec3 nextPosition = Math::SolveVerlet(
    node.position, node.prevPosition, curGravity, nodeDamping, delta);
nextPosition += externalOffset;

node.prevPosition = node.position;
node.position = nextPosition;`;

const constraintCode = `// 부모와의 거리를 원래 본 길이로 보정합니다. (Solve_Verlet 발췌)
auto &parentNode = chain.nodes[node.parentNodeIndex];
Vec3 parentToNode = node.position - parentNode.position;
const float currentDist = parentToNode.Length();

if (currentDist > 0.0001f)
{
    applyConstraintTarget(node,
        parentNode.position + (parentToNode / currentDist) * node.length);
}

if (_desc.collision.enableCollision)
{
    node.position = Compute_WorldCapsule(node, worldCapsules, node.position);
}`;

const physicsCode = `// Ensure_PhysXResources의 강체·조인트 생성 부분을 발췌했습니다.
// 체인의 시작 본은 애니메이션을 따라가는 Kinematic으로 둡니다.
node.isKinematic = (node.parentNodeIndex < 0);
PxTransform nodePose(To_PxVec3(Get_Position(node.originalWorld)),
    To_PxQuat(Extract_Rotation(node.originalWorld)));
node.actor = _physics->createRigidDynamic(nodePose);

// 생성 실패 검사는 생략했습니다.
if (node.isKinematic)
{
    node.actor->setRigidBodyFlag(PxRigidBodyFlag::eKINEMATIC, true);
    node.actor->setActorFlag(PxActorFlag::eDISABLE_GRAVITY, true);
}

// 부모·자식의 로컬 연결 프레임으로 구면 조인트를 생성합니다.
node.joint = PxSphericalJointCreate(*_physics,
    parentActor, parentLocalFrame, node.actor, childLocalFrame);

// Evaluate_Modifier에서 시뮬레이션을 진행하고 결과를 본에 반영합니다.
_scene->simulate(timeDelta);
_scene->fetchResults(true);
// 중간의 위치 추출·길이·굽힘·충돌 보정은 생략했습니다.
Write_BackToModel(model, ownerWorld, isOwnerIdle);`;

const offsetRangeCode = `// 설정한 시작·끝 본의 이름을 모델의 본 인덱스로 찾습니다.
int32 rootBoneIdx = baseModel->Get_BoneIndex(boneRange.rootBoneName);
int32 endBoneIdx = baseModel->Get_BoneIndex(boneRange.endBoneName);
if (rootBoneIdx == -1 || endBoneIdx == -1)
    continue;

// 끝 본에서 부모를 거슬러 올라가며 적용할 본을 수집합니다.
int32 currentBoneIdx = endBoneIdx;
int32 depthCount = 0;
while (currentBoneIdx != -1 && depthCount++ < 100)
{
    uniqueBones.insert(currentBoneIdx);
    if (currentBoneIdx == rootBoneIdx)
        break;
    currentBoneIdx = baseModel->Get_BoneParentIndex(currentBoneIdx);
}

// 여러 범위에서 겹친 본은 중복 없이 한 번만 등록합니다.
_boneChain.assign(uniqueBones.begin(), uniqueBones.end());`;

const offsetCode = `// OffsetAnimationModifier의 Additive 합성 부분입니다.
float effectiveAlpha = _blendAlpha * _strengthAlpha;

// 기준 포즈에서 목표 포즈까지의 회전 차이만 추출합니다.
Quat centerInv; center.rotation.Inverse(centerInv);
Quat deltaRot = target.rotation * centerInv; deltaRot.Normalize();

// 차이를 적용 강도만큼 보간한 뒤 현재 회전에 합성합니다.
Quat finalRot = Quaternion::Slerp(
    Quaternion::Identity, deltaRot, effectiveAlpha) * baseRot;
finalRot.Normalize();

// 이동도 기준 포즈와의 차이만 더합니다.
Vec3 finalPos = basePos
    + (target.position - center.position) * effectiveAlpha;

// 적용 대상 본에만 결과를 기록합니다.
model->Set_BoneLocalMatrix(boneIndex, Matrix::CreateScale(baseScale)
    * Matrix::CreateFromQuaternion(finalRot)
    * Matrix::CreateTranslation(finalPos));`;

const facialCode = `// FacialAnimationModifier::Evaluate_Modifier의 본 트랙 처리입니다.
FacialClip::TransformKey key{};
if (!_clip->Evaluate_BoneTrack(*binding.track, _timelineTimeSec, _loop, key))
    continue;

const bool additiveLocal = _clip->Is_AdditiveLocal();
const Matrix sampledLocal = Build_LocalMatrix(
    key, additiveLocal, binding.isEyeBone,
    binding.isUpperEyelidBone, blinkClosure);

// Additive Local 클립은 바인딩 시 보관한 기준 행렬과 합성합니다.
const Matrix targetLocal = additiveLocal
    ? sampledLocal * binding.baseLocal
    : sampledLocal;
model->Set_BoneLocalMatrix(
    static_cast<uint32>(binding.boneIndex), targetLocal);`;

function Code({ title, children, path, caption }: {
  title: string; children: string; path: string; caption: string;
}) {
  return <figure className="expedition-pipeline-code">
    <figcaption>{title}</figcaption><pre><code>{children}</code></pre>
    <div className="expedition-pipeline-code-footer"><p>{caption}</p>
      <IconLink href={`${expeditionLinks.source}/blob/main/${path}`} target="_blank" rel="noopener noreferrer">전체 코드 보기 ↗</IconLink>
    </div>
  </figure>;
}

function Motion({ name, width = 640, height = 360, alt, children }: {
  name: string; width?: number; height?: number; alt: string; children: string;
}) {
  return <figure className={`expedition-frame expedition-modifier-motion${name === "modifier-facial" ? " expedition-modifier-face" : ""}`}>
    <img src={siteUrl(`/images/expedition-33/${name}.gif`)} width={width} height={height} alt={alt} loading="lazy" decoding="async" />
    <figcaption>{children}</figcaption>
  </figure>;
}

export default function ExpeditionModifier() {
  return <section id="modifier" className="expedition-case expedition-pipeline expedition-modifier" aria-labelledby="expedition-modifier-title">
    <div className="container">
      <header className="expedition-pipeline-header">
        <ExpeditionChapterHeading number="03" label="ANIMATION / MODIFIER" id="expedition-modifier-title" title="Modifier 구조" technology="C++ · 상속과 다형성 · Verlet 적분 · PhysX · 본 마스킹 · Quaternion 합성" />
        <div className="expedition-pipeline-lead"><p>기본 애니메이션에 조준 동작을 섞고, 머리카락과 옷의 흔들림, 눈동자의 미세한 움직임을 더했습니다. 서로 다른 계산 방식을 공통 <code>AnimationModifier</code> 인터페이스로 연결해, 캐릭터마다 필요한 추가 움직임을 등록할 수 있도록 구성했습니다.</p><p>Offset은 선택한 본에 다른 애니메이션을 합성하고, Chain과 Physics는 본 체인에 물리적인 움직임을 적용합니다. Facial은 얼굴의 본과 모프 트랙을 재생합니다. 계산한 결과는 같은 모델의 본 처리 과정에 반영됩니다.</p></div>
      </header>

      <div id="modifier-interface" className="expedition-pipeline-block">
        <div><h4>공통 인터페이스와<br className="expedition-pipeline-heading-break" /> 적용 순서</h4><p className="expedition-pipeline-stage-tech">AnimationModifier<br />가상 함수 · 활성 상태 · 적용 강도</p></div>
        <div className="expedition-pipeline-copy"><p>캐릭터의 상태 코드에 보정 계산을 넣는 대신, 모델에 Modifier를 등록하고 <code>Evaluate_Modifier</code>로 실행하도록 했습니다. 공통 클래스에서 활성 상태와 적용 강도, 합성 방식의 설정을 제공하고, 파생 클래스는 대상 본과 계산에 필요한 데이터를 관리합니다.</p>
          <div className="expedition-modifier-contract" aria-label="AnimationModifier 상속 구조"><div><strong>AnimationModifier</strong><span>Evaluate_Modifier(ModelCom*, Δt)</span></div><ul><li><b>Offset</b><span>선택한 본에 클립 합성</span></li><li><b>Chain</b><span>Verlet 위치 계산</span></li><li><b>Physics</b><span>PhysX 강체·조인트</span></li><li><b>Facial</b><span>얼굴 본·모프 트랙</span></li></ul></div>
          <div className="expedition-character-flow"><div><strong>기본 포즈 계산</strong><span>현재 애니메이션과 블렌딩 결과를 샘플링</span></div><div><strong>Modifier 평가</strong><span>등록 순서대로 대상 본의 로컬 행렬 보정</span></div><div><strong>결합 행렬 재계산</strong><span>수정한 포즈를 본 계층에 반영</span></div></div>
          <p>애니메이션을 샘플링한 뒤 Modifier를 실행하고, 변경된 로컬 행렬로 본의 결합 행렬을 다시 계산합니다. 기본 재생 흐름을 유지하면서 애니메이션 합성, 의상 물리, 얼굴 움직임을 같은 경로에 추가할 수 있게 했습니다.</p>
        </div>
      </div>

      <div id="modifier-verlet" className="expedition-pipeline-block">
        <div><h4>ChainModifier의<br className="expedition-pipeline-heading-break" /> Verlet 적분</h4><p className="expedition-pipeline-stage-tech">현재·이전 위치 · 감쇠<br />길이·굽힘 제약 · 캡슐 충돌</p></div>
        <div className="expedition-pipeline-copy">
          <Motion name="modifier-chain" alt="ChainModifier로 움직이는 주교 의상의 케이프">ChainModifier를 적용한 주교 의상의 움직임.</Motion>
          <p>본 체인의 각 노드에 현재 위치와 이전 위치를 저장하고, 두 위치의 차이로 다음 프레임의 움직임을 계산했습니다. 여기에 감쇠와 중력, 바람·이동 관성으로 만든 외부 변위를 적용해 기본 동작 뒤에 따라오는 흔들림을 구성했습니다.</p>
          <figure className="expedition-modifier-formula"><figcaption>ChainModifier의 다음 위치 계산</figcaption><p>x다음 = x현재 + (x현재 − x이전) × d<br /><span>+ g × Δt² + 외부 변위</span></p><dl><div><dt>d</dt><dd>이전 움직임을 유지하는 비율. 1에 가까울수록 감속이 적습니다.</dd></div><div><dt>g · Δt</dt><dd>중력 가속도와 계산 시간 간격. 바람·이동 관성은 별도 변위로 더합니다.</dd></div></dl></figure>
          <p>적분 뒤에는 부모와 자식 사이의 길이를 맞추고, 기준 방향에서 굽힐 수 있는 각도를 제한하며, 캡슐 충돌로 위치를 보정합니다. 루트를 고정한 체인은 시작 위치가 애니메이션을 따라가고 끝부분이 흔들리게 했습니다. 보정된 체인의 방향은 회전으로 바꿔 본의 로컬 행렬에 반영합니다.</p>
          <details className="expedition-evidence"><summary>Verlet 위치 계산과 길이·충돌 보정 코드 보기</summary><Code title="현재·이전 위치로 다음 위치 계산" path="Engine/Private/ChainModifier.cpp" caption="위쪽은 Helper_Math.h의 SolveVerlet 계산식, 아래쪽은 ChainModifier의 호출 부분입니다. force 인자에는 이 경로에서 curGravity를 전달합니다.">{verletCode}</Code><Code title="본 길이 유지와 캡슐 충돌" path="Engine/Private/ChainModifier.cpp" caption="Solve_Verlet의 길이 제약 부분 발췌. 굽힘 제한과 모델 행렬 반영은 같은 클래스의 후속 단계에서 처리합니다.">{constraintCode}</Code></details>
        </div>
      </div>

      <div id="modifier-physics" className="expedition-pipeline-block">
        <div><h4>PhysicsModifier의<br className="expedition-pipeline-heading-break" /> 강체·조인트 구성</h4><p className="expedition-pipeline-stage-tech">PhysX · PxRigidDynamic<br />Kinematic Anchor · Spherical Joint</p></div>
        <div className="expedition-pipeline-copy">
          <Motion name="modifier-physics" width={720} height={328} alt="전투 동작에 따라 흔들리는 보스 캐릭터의 머리카락과 코트">PhysicsModifier를 적용한 머리카락과 코트. 전투 중 방향과 자세가 바뀔 때의 움직임입니다.</Motion>
          <p>가볍게 찰랑이는 움직임은 ChainModifier의 Verlet 적분으로 구현했습니다. 반면 머리카락과 단단한 천은 형태를 유지하면서 충돌에 반응하는 느낌을 맞추기 어려웠습니다. 이 부위에는 PhysX 강체와 조인트를 사용하는 PhysicsModifier를 적용했습니다.</p><p>본 체인에 강체를 만들고 부모·자식을 구면 조인트로 연결했습니다. 체인의 시작 본과 앵커는 애니메이션을 따라가는 Kinematic으로 두고, 뒤쪽 본은 중력과 외력에 반응하도록 구성했습니다. 조인트의 연결 프레임과 회전 한계를 설정해 붙어 있는 부분을 유지하면서 움직이게 했습니다.</p>
          <div className="expedition-character-flow"><div><strong>애니메이션에 고정</strong><span>시작 본·앵커의 Kinematic 위치 갱신</span></div><div><strong>PhysX 시뮬레이션</strong><span>강체와 조인트에 중력·바람·관성 적용</span></div><div><strong>본에 결과 반영</strong><span>길이·굽힘·충돌 보정 후 로컬 행렬 갱신</span></div></div>
          <p>시뮬레이션 결과를 읽은 뒤 길이와 굽힘, 충돌을 보정하고, 출력 강도와 회전 보간을 거쳐 모델에 반영합니다. 애니메이션 포즈를 복원 기준으로 사용해 원래 형태와 물리 움직임 사이를 조절하도록 했습니다.</p>
          <h5 className="expedition-pipeline-subtitle">머리카락과 옷에 적용</h5><p>보스 캐릭터의 코트에는 PhysicsModifier를, 팔 장식에는 ChainModifier를 모델에 등록해 적용했습니다. 분리된 머리카락은 머리 소켓을 포함한 월드 행렬을 갱신한 뒤 PhysicsModifier의 평가 함수를 호출하고, 몸체 모델의 충돌 정보를 참조하도록 연결했습니다. 부위마다 필요한 시점과 대상 본을 지정하면서 같은 인터페이스로 보정을 실행했습니다.</p>
          <details className="expedition-evidence"><summary>강체·조인트 생성과 시뮬레이션 코드 보기</summary><Code title="애니메이션에 연결한 강체 체인" path="Engine/Private/PhysicsModifier.cpp" caption="서로 다른 함수에서 관련 부분을 발췌했습니다. PhysX의 시뮬레이션 결과를 자체 모델의 본 행렬로 돌려주는 연결 구조입니다.">{physicsCode}</Code></details>
        </div>
      </div>

      <div id="modifier-offset" className="expedition-pipeline-block">
        <div><h4>Offset으로<br className="expedition-pipeline-heading-break" /> 애니메이션 합성</h4><p className="expedition-pipeline-stage-tech">본 범위 마스킹 · Additive / Replace<br />Quaternion Slerp · Blend In / Out</p></div>
        <div className="expedition-pipeline-copy">
          <div className="expedition-modifier-media-pair">
            <Motion name="modifier-offset-aim" alt="이동 중 조준 방향에 따라 팔을 위아래로 움직이는 플레이어 캐릭터">걷기 + 상체 조준</Motion>
            <Motion name="modifier-offset-hit" width={720} height={328} alt="기본 동작에 피격 포즈를 합성해 타격에 반응하는 보스 캐릭터">기본 동작 + 피격 반응</Motion>
          </div>
          <p><code>OffsetAnimationModifier</code>는 모델의 지정한 본 범위에 다른 애니메이션을 합성하는 Modifier입니다. 적용할 클립과 시작·끝 본, 합성 방식과 적용 강도를 설정해 조준, 방향별 걷기, 피격 반응에 같은 구조를 사용했습니다.</p>
          <div className="expedition-character-flow" aria-label="OffsetAnimationModifier의 적용 과정"><div><strong>본 범위 지정</strong><span>시작·끝 본 사이의 계층을 수집해 적용 대상 구성</span></div><div><strong>클립 포즈 계산</strong><span>입력에 따른 포즈 선택 또는 재생 시간으로 샘플링</span></div><div><strong>선택한 본에 합성</strong><span>Additive / Replace와 적용 비율로 로컬 행렬 갱신</span></div></div>
          <p>시작 본과 끝 본을 이름으로 지정하면, 끝 본에서 부모를 따라 시작 본까지 올라가며 적용할 본을 수집합니다. 여러 범위를 등록할 수 있고 겹치는 본은 중복을 제거합니다. 이렇게 구성한 대상 본에만 결과를 기록해, 필요한 부위를 선택적으로 합성하도록 했습니다.</p>
          <div className="expedition-modifier-offset-map" aria-label="Offset의 두 가지 합성 방식"><article><h5>Additive · 포즈 차이 합성</h5><p>클립의 기준 포즈와 목표 포즈 사이의 차이를 구해 현재 동작에 더합니다. 회전 차이는 Quaternion Slerp로 적용 강도만큼 보간하고, 이동 차이는 현재 위치에 가중해 더합니다.</p></article><article><h5>Replace · 클립 포즈로 보간</h5><p>대상 본의 현재 포즈에서 클립의 목표 포즈로 보간합니다. 회전은 Slerp, 이동과 크기는 Lerp로 계산해 적용 비율에 따라 클립의 동작을 반영합니다.</p></article></div>
          <p>목표 포즈는 조준 각도에 따라 클립의 기준·상향·하향 포즈를 보간하거나, 클립을 재생하며 시간에 따라 샘플링하도록 구성했습니다. Blend In / Out으로 진입·해제 시 적용 비율을 조절하고, 이동만 반영할 본은 별도로 지정해 기존 회전을 유지할 수 있게 했습니다.</p>
          <h5 className="expedition-pipeline-subtitle">이동 중 조준과 대기 중 피격 반응에 적용</h5>
          <p>플레이어의 자유 조준에서는 카메라의 위아래 각도에 따른 조준 포즈를 상체에 Additive로 합성하고, 하체에는 이동 방향별 걷기 클립을 Replace로 적용했습니다. 보스의 피격 반응은 대기 동작 위에 상체 피격 클립을 한 번 재생하고 서서히 해제해, 기본 동작을 유지하면서 타격에 반응하도록 했습니다.</p>
          <details className="expedition-evidence"><summary>적용 본 범위 구성과 Offset 합성 코드 보기</summary><Code title="시작·끝 본으로 적용 범위 구성" path="Engine/Private/OffsetAnimationModifier.cpp" caption="Initialize의 본 범위 수집 부분을 발췌했습니다. 범위마다 부모 계층을 따라 본을 수집하고, 중복을 제거한 목록을 평가에 사용합니다.">{offsetRangeCode}</Code><Code title="선택한 본에 회전·이동 차이 합성" path="Engine/Private/OffsetAnimationModifier.cpp" caption="Additive 계산의 관련 부분 발췌. translationOnlyBones에 등록된 본의 회전을 유지하는 분기는 생략했습니다. 적용 강도는 활성 전환 비율과 strengthAlpha의 곱입니다.">{offsetCode}</Code></details>
        </div>
      </div>

      <div id="modifier-facial" className="expedition-pipeline-block">
        <div><h4>눈동자와 얼굴의<br className="expedition-pipeline-heading-break" /> 미세 움직임</h4><p className="expedition-pipeline-stage-tech">FacialAnimationModifier<br />본 트랙 · Additive Local · Morph</p></div>
        <div className="expedition-pipeline-copy">
          <Motion name="modifier-facial" width={420} height={560} alt="메뉴 화면에서 눈동자와 얼굴이 미세하게 움직이는 플레이어 캐릭터">메뉴 대기 중 눈동자와 얼굴에 이어지는 미세 움직임.</Motion>
          <p>눈동자 움직임은 Facial 클립의 눈 본 트랙을 재생해 적용했습니다. 트랙 이름을 모델의 본과 연결하고, 시간에 따라 샘플링한 변환을 얼굴 모델에 반영합니다. Additive Local 클립은 바인딩할 때 보관한 기준 로컬 행렬과 합성하며, 눈 본의 회전량은 별도 비율로 조절했습니다.</p><p>실제 게임 메뉴에서는 캐릭터의 머리 모델에 미세 움직임 클립을 반복 재생하도록 연결했습니다. 눈·눈꺼풀의 본 움직임과 모프 트랙을 같은 Modifier에서 처리해, 대기 중에도 얼굴에 작은 움직임이 이어지게 했습니다.</p>
          <details className="expedition-evidence"><summary>얼굴 본 트랙의 샘플링과 합성 코드 보기</summary><Code title="Facial 클립 → 얼굴 본의 로컬 행렬" path="Engine/Private/FacialAnimationModifier.cpp" caption="Evaluate_Modifier의 본 트랙 처리 발췌. 트랙 바인딩, 눈 회전량 보정과 모프 적용은 같은 Modifier의 별도 단계에서 처리합니다.">{facialCode}</Code></details>
          <p>이 구조로 기본 클립의 동작에 조준·걷기 합성, 머리카락·옷의 후속 움직임, 눈동자의 미세 움직임을 더했습니다. 새로운 보정이 필요할 때도 상태별 동작과 분리해 Modifier를 구현하고 모델에 연결할 수 있도록 했습니다.</p>
          <IconLink className="expedition-pipeline-next expedition-modifier-next" href="#ai">맵·셰이더 데이터 통합과 AI 활용 →</IconLink>
        </div>
      </div>
    </div>
  </section>;
}
